import { useEffect, type ReactNode } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { appEnv, releaseVersion } from "../lib/config";
import { CookieConsentProvider, openCookiePreferences } from "./cookie-consent";
import "../styles/marketing.css";

/**
 * Subtle on-scroll reveal. Elements with the `.mkt-reveal` class fade/slide in
 * as they enter the viewport. Fully progressive: without JS (or Intersection
 * Observer, e.g. in tests) content is shown immediately, and reduced-motion
 * users never see the transform (handled in CSS).
 */
function useReveal(): void {
  // Re-run on every route change. The marketing layout stays mounted across
  // client-side navigations, so a run-once effect would only ever observe the
  // FIRST page's `.mkt-reveal` elements — any page reached by an in-app link
  // (e.g. the legal pages from the footer) would keep its content stuck at
  // opacity:0 and appear blank. Keying on the pathname re-scans after each nav.
  const { pathname } = useLocation();
  useEffect(() => {
    const root = document.querySelector(".mkt");
    if (!root || typeof IntersectionObserver === "undefined") return;
    root.classList.add("js-reveal");
    const els = Array.from(root.querySelectorAll(".mkt-reveal:not(.is-revealed)"));
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-revealed");
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 },
    );
    for (const el of els) io.observe(el);
    return () => io.disconnect();
  }, [pathname]);
}

/**
 * Public MARKETING site building blocks (odinforge.io apex).
 *
 * The marketing site is DARK and premium, built on the ODIN design tokens; it
 * shares the app's brand but has its own layout. All of the styling here lives
 * under the `.mkt` root class (see marketing.css) so it is fully scoped away from
 * the authenticated app. Importing marketing.css here means the theme is only ever
 * loaded where a marketing component is used.
 *
 * TRUTHFULNESS: nothing in these components fabricates testimonials, customer or
 * server counts, uptime guarantees, SLAs or certifications. Availability is stated
 * honestly ("no uptime promises").
 */

export type MktVariant = "primary" | "secondary" | "ghost";

/**
 * Link-based button. Use `to` for in-app routes (react-router) and `href` for
 * external / mailto targets. The primary "Get started" CTA points at the in-repo
 * `/login` route, never an absolute odinforge URL.
 */
export function MktButton({
  children,
  variant = "primary",
  to,
  href,
  className,
}: {
  children: ReactNode;
  variant?: MktVariant;
  to?: string;
  href?: string;
  className?: string;
}): JSX.Element {
  const cls = `mkt-btn mkt-btn--${variant}${className ? ` ${className}` : ""}`;
  if (href !== undefined) {
    return (
      <a className={cls} href={href}>
        {children}
      </a>
    );
  }
  return (
    <Link className={cls} to={to ?? "/"}>
      {children}
    </Link>
  );
}

export function MktBrand(): JSX.Element {
  return (
    <Link to="/" className="mkt-brand" aria-label="ODIN home">
      <img src="/brand/odin/odin-mark-128.png" alt="" className="mkt-brand__mark-img" aria-hidden="true" />
      <span className="mkt-brand__word">ODIN</span>
    </Link>
  );
}

const NAV_LINKS: ReadonlyArray<{ to: string; label: string }> = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/status", label: "Status" },
];

function MktNav(): JSX.Element {
  return (
    <nav className="mkt-nav" aria-label="Primary">
      <div className="mkt-container mkt-nav__inner">
        <MktBrand />
        <div className="mkt-nav__links">
          {NAV_LINKS.map((item) => (
            <Link key={item.to} to={item.to}>
              {item.label}
            </Link>
          ))}
        </div>
        <div className="mkt-nav__cta">
          <MktButton to="/login" variant="secondary">
            Sign in
          </MktButton>
          <MktButton to="/login" variant="primary">
            Start free
          </MktButton>
        </div>
      </div>
    </nav>
  );
}

function MktFooter(): JSX.Element {
  return (
    <footer className="mkt-footer">
      <div className="mkt-container">
        <div className="mkt-footer__cols">
          <div className="mkt-footer__col mkt-footer__brand">
            <MktBrand />
            <p className="mkt-footer__blurb">
              A calm control plane for one Discord community — design, review and safely deploy, with
              you approving every change.
            </p>
            <p className="mkt-footer__copyright">© {new Date().getFullYear()} ODIN</p>
          </div>
          <div className="mkt-footer__col">
            <h4>Product</h4>
            <ul>
              <li>
                <Link to="/features">Features</Link>
              </li>
              <li>
                <Link to="/pricing">Pricing</Link>
              </li>
              <li>
                <Link to="/status">Status</Link>
              </li>
            </ul>
          </div>
          <div className="mkt-footer__col">
            <h4>Company</h4>
            <ul>
              <li>
                <Link to="/about">About</Link>
              </li>
              <li>
                <Link to="/contact">Contact</Link>
              </li>
              {/* Part 10.2 — the footer support link. It goes to the signed-in desk
                  rather than a mailto: a customer who is already signed in lands
                  straight in a conversation attached to their account, and everyone
                  else is one sign-in away, with /contact directly above for people
                  who have no account at all. */}
              <li>
                <Link to="/app/support">Support</Link>
              </li>
              <li>
                <Link to="/security">Security</Link>
              </li>
            </ul>
          </div>
          <div className="mkt-footer__col">
            <h4>Legal</h4>
            <ul>
              <li>
                <Link to="/privacy">Privacy</Link>
              </li>
              <li>
                <Link to="/terms">Terms</Link>
              </li>
              <li>
                <Link to="/legal/cookies">Cookie Policy</Link>
              </li>
              <li>
                <Link to="/legal/acceptable-use">Acceptable Use</Link>
              </li>
              <li>
                <Link to="/legal/dpa">Data Processing (DPA)</Link>
              </li>
              <li>
                <Link to="/legal/subprocessors">Subprocessors</Link>
              </li>
              <li>
                <Link to="/legal/refunds-and-cancellations">Refunds &amp; Cancellations</Link>
              </li>
              <li>
                <Link to="/legal/ai-transparency">AI Transparency</Link>
              </li>
              <li>
                <button type="button" className="mkt-footer__linkbtn" onClick={openCookiePreferences}>
                  Cookie preferences
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="mkt-footer__meta">
          <p>
            ODIN is rolling out carefully. Live deployment is locked by default and we make no uptime
            promises — see the <Link to="/status">status page</Link> for the current state.
          </p>
          <p>
            <span className="mkt-mono">{appEnv}</span> · build{" "}
            <span className="mkt-mono">{releaseVersion}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

/**
 * Public marketing layout: light top nav + routed content + footer. Renders
 * `children` when given (used directly and in tests) and otherwise an `<Outlet/>`
 * for nested router routes.
 */
export function MarketingLayout({ children }: { children?: ReactNode }): JSX.Element {
  useReveal();
  return (
    <CookieConsentProvider>
      <div className="mkt">
        <a className="skip-link" href="#mkt-main">
          Skip to content
        </a>
        <MktNav />
        <main id="mkt-main">{children ?? <Outlet />}</main>
        <MktFooter />
      </div>
    </CookieConsentProvider>
  );
}

/** Gradient-accented span for a word or phrase inside a heading. */
export function MktAccent({ children }: { children: ReactNode }): JSX.Element {
  return <span className="mkt-grad">{children}</span>;
}

export function MktHero({
  kicker,
  title,
  subhead,
  visual,
  backdrop,
  children,
}: {
  kicker?: string;
  title: ReactNode;
  subhead?: string;
  visual?: ReactNode;
  /** Optional full-bleed background image (e.g. the ODIN brand banner). When set,
   * the split visual is omitted and copy sits over a legibility gradient. */
  backdrop?: string;
  children?: ReactNode;
}): JSX.Element {
  const cls = backdrop
    ? "mkt-hero mkt-hero--banner"
    : `mkt-hero${visual ? " mkt-hero--split" : ""}`;
  return (
    <header className={cls}>
      {backdrop ? (
        <div
          className="mkt-hero__backdrop"
          style={{ backgroundImage: `url(${backdrop})` }}
          aria-hidden="true"
        />
      ) : null}
      <div className="mkt-container mkt-hero__inner">
        <div className="mkt-hero__copy">
          {kicker ? <span className="mkt-hero__kicker">{kicker}</span> : null}
          <h1>{title}</h1>
          {subhead ? <p className="mkt-hero__sub">{subhead}</p> : null}
          {children ? <div className="mkt-hero__actions">{children}</div> : null}
        </div>
        {!backdrop && visual ? <div className="mkt-hero__visual mkt-reveal">{visual}</div> : null}
      </div>
    </header>
  );
}

/**
 * A stylized "app window" preview of the ODIN dashboard, built with the design
 * tokens (not a screenshot, so it never goes stale). Decorative only.
 */
export function MktAppPreview(): JSX.Element {
  return (
    <div className="mkt-appwin" aria-hidden="true">
      <div className="mkt-appwin__bar">
        <span className="mkt-appwin__dot" />
        <span className="mkt-appwin__dot" />
        <span className="mkt-appwin__dot" />
        <span className="mkt-appwin__addr">app.odinforge.io</span>
      </div>
      <div className="mkt-appwin__body">
        <div className="mkt-appwin__side">
          <span className="mkt-appwin__brand" />
          {Array.from({ length: 7 }).map((_, i) => (
            <span key={i} className={`mkt-appwin__nav${i === 0 ? " is-active" : ""}`} />
          ))}
        </div>
        <div className="mkt-appwin__main">
          <span className="mkt-appwin__title" />
          <div className="mkt-appwin__cards">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="mkt-appwin__card">
                <span className={`mkt-appwin__chip mkt-appwin__chip--${i % 3}`} />
                <span className="mkt-appwin__val" />
                <span className="mkt-appwin__bar2" />
              </div>
            ))}
          </div>
          <div className="mkt-appwin__panel" />
        </div>
      </div>
    </div>
  );
}

export function MktSection({
  kicker,
  title,
  lead,
  children,
  id,
}: {
  kicker?: string;
  title?: string;
  lead?: string;
  children?: ReactNode;
  id?: string;
}): JSX.Element {
  return (
    <section className="mkt-section mkt-reveal" id={id}>
      <div className="mkt-container">
        {kicker ? <p className="mkt-section__kicker">{kicker}</p> : null}
        {title ? <h2>{title}</h2> : null}
        {lead ? <p className="mkt-section__lead">{lead}</p> : null}
        {children}
      </div>
    </section>
  );
}

export function MktStat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}): JSX.Element {
  return (
    <div className="mkt-stat">
      <div className="mkt-stat__label">{label}</div>
      <div className="mkt-stat__value">{value}</div>
      <p className="mkt-stat__sub">{sub}</p>
    </div>
  );
}

export function MktFeature({
  title,
  desc,
  icon,
  children,
}: {
  title: string;
  desc?: string;
  icon?: ReactNode;
  children?: ReactNode;
}): JSX.Element {
  return (
    <article className="mkt-card">
      {icon ? (
        <span className="mkt-card__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <h3>{title}</h3>
      {desc ? <p>{desc}</p> : null}
      {children}
    </article>
  );
}

/** A numbered "how it works" pillar. Steps are numbered via CSS counters. */
export function MktPillar({ title, desc }: { title: string; desc: string }): JSX.Element {
  return (
    <article className="mkt-pillar">
      <span className="mkt-pillar__step" aria-hidden="true" />
      <h3>{title}</h3>
      <p>{desc}</p>
    </article>
  );
}

function MktCheck(): JSX.Element {
  return (
    <span className="mkt-trust__check" aria-hidden="true">
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4">
        <path d="M4 10.5 8 14.5 16 5.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/** A single safety/trust point with a check glyph. */
export function MktTrustItem({ title, desc }: { title: string; desc: string }): JSX.Element {
  return (
    <div className="mkt-trust__item">
      <MktCheck />
      <div>
        <h3>{title}</h3>
        <p>{desc}</p>
      </div>
    </div>
  );
}

/**
 * A compact row of reassurance points (e.g. "Free to start · No credit card").
 * Rendered as a list for accessibility; separators are decorative (CSS).
 */
export function MktBadgeRow({ items }: { items: ReadonlyArray<string> }): JSX.Element {
  return (
    <ul className="mkt-badgerow">
      {items.map((item) => (
        <li key={item} className="mkt-badgerow__item">
          {item}
        </li>
      ))}
    </ul>
  );
}

/**
 * A prominent, centered call-to-action band. Pass the CTA buttons as children.
 * `note` renders a small reassurance line under the buttons.
 */
export function MktCtaBand({
  kicker,
  title,
  lead,
  note,
  children,
  id,
}: {
  kicker?: string;
  title: string;
  lead?: string;
  note?: ReactNode;
  children?: ReactNode;
  id?: string;
}): JSX.Element {
  return (
    <div className="mkt-container">
      <div className="mkt-cta mkt-reveal" id={id}>
        {kicker ? <p className="mkt-cta__kicker">{kicker}</p> : null}
        <h2>{title}</h2>
        {lead ? <p>{lead}</p> : null}
        {children ? <div className="mkt-hero__actions">{children}</div> : null}
        {note ? <p className="mkt-cta__note">{note}</p> : null}
      </div>
    </div>
  );
}

/** Accessible FAQ list. Wraps a set of <MktFaqItem> accordions. */
export function MktFaq({ children }: { children: ReactNode }): JSX.Element {
  return <div className="mkt-faq">{children}</div>;
}

/**
 * A single FAQ entry rendered as a native <details>/<summary> accordion, so it is
 * keyboard-accessible and works without JavaScript. `defaultOpen` expands it.
 */
export function MktFaqItem({
  question,
  children,
  defaultOpen = false,
}: {
  question: string;
  children: ReactNode;
  defaultOpen?: boolean;
}): JSX.Element {
  return (
    <details className="mkt-faq__item" open={defaultOpen}>
      <summary className="mkt-faq__q">
        <span>{question}</span>
        <span className="mkt-faq__icon" aria-hidden="true" />
      </summary>
      <div className="mkt-faq__a">{children}</div>
    </details>
  );
}

/**
 * BALDER showcase — a distinct, blue-accented "meet the AI companion" band that
 * uses the BALDER brand banner. Truthful: BALDER advises, drafts and previews but
 * never deploys or approves on its own.
 */
export function MktBalderShowcase(): JSX.Element {
  const points: ReadonlyArray<{ t: string; d: string }> = [
    { t: "Reads your server", d: "Inspects roles, channels and permissions and explains how it's set up." },
    { t: "Drafts the change", d: "Prepares blueprint edits for roles, channels, onboarding and moderation." },
    { t: "Previews everything", d: "Produces a dry run of exactly what would be created, changed or removed." },
    { t: "Never acts alone", d: "Advises and explains in plain language — you approve every change." },
  ];
  return (
    <section className="mkt-section mkt-balder mkt-reveal" id="balder">
      <div className="mkt-container mkt-balder__inner">
        <div className="mkt-balder__copy">
          <p className="mkt-section__kicker mkt-balder__kicker">Your AI companion</p>
          <h2>
            Meet <span className="mkt-grad mkt-grad--blue">BALDER</span>
          </h2>
          <p className="mkt-section__lead">
            BALDER is the assistant built into ODIN — a calm co-pilot that turns Discord's complexity into
            plain language. Ask a question, and BALDER helps you shape, understand and safely preview a
            change before anything reaches your community.
          </p>
          <ul className="mkt-balder__list">
            {points.map((p) => (
              <li key={p.t} className="mkt-balder__item">
                <span className="mkt-balder__check" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4">
                    <path d="M4 10.5 8 14.5 16 5.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div>
                  <strong>{p.t}</strong>
                  <span>{p.d}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mkt-hero__actions">
            <MktButton to="/login" variant="primary">
              Start free
            </MktButton>
            <MktButton href="#how-it-works" variant="secondary">
              See how it works
            </MktButton>
          </div>
        </div>
        <div className="mkt-balder__visual mkt-reveal">
          <div className="mkt-balder__frame">
            <img
              src="/brand/balder/balder-banner.webp"
              alt="BALDER — your AI companion"
              loading="lazy"
              width="1600"
              height="900"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * A full pricing plan card for the /pricing page. Humane: no "Most Popular"
 * badge, no scarcity; the Free plan is highlighted as the starting point rather
 * than the most expensive one. Prices that are not finalised show "Contact us".
 */
export function MktPlan({
  name,
  price,
  cadence,
  priceNote,
  usageNote,
  desc,
  features,
  featured = false,
  cta,
}: {
  name: string;
  price: string;
  cadence?: string;
  /** Small, visually-secondary label under the price (e.g. "VAT included"). */
  priceNote?: string;
  /** Short usage disclosure under the description (e.g. the BALDER AI allowance note). */
  usageNote?: string;
  desc: string;
  features: ReadonlyArray<string>;
  featured?: boolean;
  cta: ReactNode;
}): JSX.Element {
  return (
    <div className={`mkt-plan${featured ? " mkt-plan--featured" : ""}`}>
      <span className="mkt-plan__name">{name}</span>
      <div className="mkt-plan__price">
        {price}
        {cadence ? <small> {cadence}</small> : null}
      </div>
      {priceNote ? <p className="mkt-plan__pricenote">{priceNote}</p> : null}
      <p className="mkt-plan__desc">{desc}</p>
      {usageNote ? <p className="mkt-plan__usage">{usageNote}</p> : null}
      <ul className="mkt-plan__list">
        {features.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      {cta}
    </div>
  );
}

/** A single pricing-teaser tile linking to the full /pricing page. */
export function MktPrice({
  name,
  amount,
  desc,
}: {
  name: string;
  amount: string;
  desc: string;
}): JSX.Element {
  return (
    <div className="mkt-price">
      <span className="mkt-price__name">{name}</span>
      <span className="mkt-price__amount">{amount}</span>
      <p>{desc}</p>
    </div>
  );
}
